import { Sun, Clock, Clock4, SunDim, Sunrise } from 'lucide-react';
import type { SolarForecast } from '../../types';

interface SolarForecastCardProps {
  forecast: SolarForecast;
  compact?: boolean;
}

export function SolarForecastCard({ forecast, compact = false }: SolarForecastCardProps) {
  if (compact) {
    return (
      <div className="card p-3 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <Sun className="w-5 h-5 text-amber-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Solar Forecast</h3>
        </div>

        {/* Grid items - fill available space */}
        <div className="flex-1 flex flex-col gap-2">
          {/* Row 1: This Hour | Next Hour */}
          <div className="grid grid-cols-2 gap-2 flex-1">
            <ForecastItem
              icon={<Clock className="w-4 h-4" />}
              iconColor="text-orange-500"
              value={`${Math.round(forecast.this_hour_wh)} Wh`}
              label="This Hour"
            />
            <ForecastItem
              icon={<Clock4 className="w-4 h-4" />}
              iconColor="text-orange-600"
              value={`${Math.round(forecast.next_hour_wh)} Wh`}
              label="Next Hour"
            />
          </div>

          {/* Row 2: Remaining Today | Today Total */}
          <div className="grid grid-cols-2 gap-2 flex-1">
            <ForecastItem
              icon={<SunDim className="w-4 h-4" />}
              iconColor="text-green-500"
              value={`${forecast.remaining_today_kwh.toFixed(1)} kWh`}
              label="Remaining Today"
            />
            <ForecastItem
              icon={<Sun className="w-4 h-4" />}
              iconColor="text-amber-500"
              value={`${forecast.today_total_kwh.toFixed(1)} kWh`}
              label="Generated Today"
            />
          </div>

          {/* Row 3: Tomorrow (full width) */}
          <div className="flex-1">
            <ForecastItem
              icon={<Sunrise className="w-4 h-4" />}
              iconColor="text-purple-500"
              value={`${forecast.tomorrow_kwh.toFixed(1)} kWh`}
              label="Tomorrow"
              fullWidth
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Sun className="w-5 h-5 text-amber-500" />
        <h3 className="font-semibold text-gray-900 dark:text-white">Solar Forecast</h3>
      </div>

      {/* Forecast Grid */}
      <div className="grid grid-cols-2 gap-3">
        <ForecastItem
          icon={<Clock className="w-4 h-4" />}
          iconColor="text-orange-500"
          value={`${forecast.this_hour_wh.toLocaleString('en-AU')} Wh`}
          label="This Hour"
        />
        <ForecastItem
          icon={<Clock4 className="w-4 h-4" />}
          iconColor="text-orange-600"
          value={`${forecast.next_hour_wh.toLocaleString('en-AU')} Wh`}
          label="Next Hour"
        />
        <ForecastItem
          icon={<SunDim className="w-4 h-4" />}
          iconColor="text-green-500"
          value={`${forecast.remaining_today_kwh.toFixed(1)} kWh`}
          label="Remaining Today"
        />
        <ForecastItem
          icon={<Sun className="w-4 h-4" />}
          iconColor="text-amber-500"
          value={`${forecast.today_total_kwh.toFixed(1)} kWh`}
          label="Generated Today"
        />
      </div>

      {/* Tomorrow - full width */}
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
        <ForecastItem
          icon={<Sunrise className="w-4 h-4" />}
          iconColor="text-purple-500"
          value={`${forecast.tomorrow_kwh.toFixed(1)} kWh`}
          label="Tomorrow"
          fullWidth
        />
      </div>
    </div>
  );
}

interface ForecastItemProps {
  icon: React.ReactNode;
  iconColor: string;
  value: string;
  label: string;
  fullWidth?: boolean;
}

function ForecastItem({ icon, iconColor, value, label, fullWidth }: ForecastItemProps) {
  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-brand-navy-light/50 h-full ${fullWidth ? 'justify-center' : ''}`}>
      <div className={iconColor}>{icon}</div>
      <div className={fullWidth ? 'text-center' : ''}>
        <p className={`font-semibold text-gray-900 dark:text-white ${fullWidth ? 'text-base' : 'text-sm'}`}>
          {value}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      </div>
    </div>
  );
}
